import { Component, createHook } from '@atomify/hooks';

declare global {
    interface Window {
        ShadyCSS: any;
    }
    interface ShadowRoot {
        adoptedStyleSheets: CSSStyleSheet[];
    }

    interface Document {
        adoptedStyleSheets: CSSStyleSheet[];
    }

    interface CSSStyleSheet {
        replaceSync(cssText: string): void;
    }
}

interface CSSResult {
    cssText: string;
    styleSheet: CSSStyleSheet | null;
}

type CSSResultOrNative = CSSResult | CSSStyleSheet;

const supportShadyCSS = window.ShadyCSS && !window.ShadyCSS.nativeShadow;
const CSS_SAVE_TOKEN = Symbol('atomify.cssSaveToken');
const styleCache = new Map<string, HTMLTemplateElement>();
const APPLIED_STYLES: string[] = [];

/**
 * Checks if adopting stylesheet is supported.
 */
const supportsAdoptingStyleSheets = () =>
    'adoptedStyleSheets' in Document.prototype && 'replace' in CSSStyleSheet.prototype;

/**
 * Generates a template based necessary fallbacks needed for browsers that do not support Custom elements or ShadowDom
 * Reference: https://github.com/webcomponents/webcomponentsjs
 * @param {Component} target
 * @param {HTMLTemplateElement} template
 */
function bindShadyRoot(target: Component, template: HTMLTemplateElement) {
    window.ShadyCSS.prepareTemplate(template, target.$cmpMeta$.$tagName$);
    window.ShadyCSS.styleElement(target);
}

const applyShadowdomStyles = (component: Component, styles: string) => {
    const container = component.container;
    const componentName = component.$cmpMeta$.$tagName$;
    let styleTmp: HTMLTemplateElement | undefined;

    if (!styleCache.has(componentName)) {
        styleTmp = document.createElement('template');

        styleTmp.innerHTML = `${
            component.hasShadowDom && !supportsAdoptingStyleSheets() && styles
                ? `<style style-hook>${styles}</style>`
                : ''
        }`;
    } else {
        styleTmp = styleCache.get(componentName);
    }

    if (styleTmp) {
        // Apply polyfill when shady polyfill is available and the component has shadowdom
        if (supportShadyCSS && component.hasShadowDom) {
            bindShadyRoot(component, styleTmp);
        }

        container.appendChild(document.importNode(styleTmp.content, true));
    }
};

/**
 * This is unsafe because untrusted CSS text can be used to phone home
 * or exfiltrate data to an attacker controlled site. Take care to only use
 * this with trusted input.
 * @param {string} css
 * @returns {StyleObject}
 */
const unsafeCSS = (cssString: unknown): CSSResult =>
    createCSSResult(String(cssString), CSS_SAVE_TOKEN);

/**
 * @param {string} css
 * @param {symbol} token
 * @returns {({ cssText: string; styleSheet: CSSStyleSheet | null })}
 */
const createCSSResult = (
    css: string,
    token: symbol,
): { cssText: string; styleSheet: CSSStyleSheet | null } => {
    if (token !== CSS_SAVE_TOKEN)
        throw new Error(`${css} is not supported. Use 'unsafeCSS' if you want to use: ${css}`);

    let styleSheet: CSSStyleSheet | null = null;
    const cssText = css;

    if (supportsAdoptingStyleSheets()) {
        styleSheet = new CSSStyleSheet();
        styleSheet.replaceSync(cssText);
    }

    return {
        cssText,
        styleSheet,
    };
};

/**
 * Applies adoptedStyleSheets to the Atomify component.
 * @param { Component } root
 * @param { StyleObject } css
 */
const adoptStyles = (root: Component, styles: Array<CSSResultOrNative>) => {
    const hasShadowDom = root.hasShadowDom;
    const componentName = root.$cmpMeta$.$tagName$;

    // Add addopted stylesheets when it is supported
    if (supportsAdoptingStyleSheets() && hasShadowDom) {
        const CSSRoot = root.container;
        if (!(CSSRoot instanceof HTMLElement))
            CSSRoot.adoptedStyleSheets = styles.map((s) => {
                return s instanceof StyleSheet ? s : s.styleSheet!;
            });
    } else if (APPLIED_STYLES.indexOf(componentName) === -1) {
        const combinedStyleArray = styles
            .map((style) => ('cssText' in style ? style.cssText : null))
            .join('');

        // When adopted stylesheet is not supported but shadowdom is apply it to the shadowroot.
        // Else it is being applied to the head.
        if (hasShadowDom) {
            // Cache the styles so it can be reused instead of creating a style tag again.
            root.styles = combinedStyleArray;
            applyShadowdomStyles(root, combinedStyleArray);
        } else {
            const styleElement = document.createElement('style');
            styleElement.textContent = combinedStyleArray;
            styleElement.setAttribute('scope', componentName);

            document.head.appendChild(styleElement);
            APPLIED_STYLES.push(componentName);
        }
    }
};

export const useStylesheet = (style: string) => {
    const css = unsafeCSS(style);
    const styles = Array.isArray(css) ? [...css] : [css];

    return createHook({
        onDidLoad: (element) => adoptStyles(element, styles),
        onUpdate: (element) => adoptStyles(element, styles),
    });
};
